import React from "react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";

export default function TermsPage() {
  return (
    <>
      <PageHero
        eyebrow="Terms & Disclosures"
        title="Terms of service and legal disclosures."
        lead="The following terms apply to your use of OGONotary.com and the services provided by OGO Notary & Signing Services — a division of OldGuyOutside, LLC. By engaging us or submitting any form on this site, you acknowledge and agree to these terms."
      />
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 space-y-10 text-slate-300 leading-relaxed">
          <Block title="1. Not Legal Advice">
            <p>
              OGO Notary & Signing Services is not a law firm and does not
              provide legal advice. A Florida Notary Public is prohibited by
              law from drafting legal documents, selecting forms, or
              interpreting the contents of any document. Nothing on
              OGONotary.com — including blog posts, guides, checklists, app
              previews, or chat responses — is intended as legal, financial,
              tax, immigration, or estate-planning advice. For legal
              questions, consult a licensed attorney in the relevant
              jurisdiction.
            </p>
          </Block>

          <Block title="2. Scope of Services">
            <p>
              We provide notarial acts authorized under our Florida
              commission, including:
            </p>
            <ul className="mt-4 space-y-2 list-disc list-inside">
              <li>
                <span className="text-white">Mobile Notary</span> — in-person
                notarization throughout Brevard County, Florida and
                surrounding service areas.
              </li>
              <li>
                <span className="text-white">Loan Signing Agent</span> —
                NNA-certified, background-screened signing services for
                title companies, escrow officers, mortgage brokers, and law
                firms.
              </li>
              <li>
                <span className="text-white">Remote Online Notarization (RON)</span>{" "}
                — performed under our Florida commission using a
                state-approved audio-video and identity-verification
                technology provider; available nationally and
                internationally where lawful.
              </li>
              <li>
                <span className="text-white">Apostille / Authentication Agent</span>{" "}
                — Florida Secretary of State Apostille handling for Hague
                Convention countries, plus chain-authentication coordination
                for non-Hague destinations.
              </li>
            </ul>
            <p className="mt-4">
              All services are subject to applicable Florida law and federal
              law, including statutory fee maximums, journal and recording
              retention requirements, RON technology standards, and
              identification rules.
            </p>
          </Block>

          <Block title="3. Statutory Fees & Service Fees">
            <p>
              Notarial-act fees comply with the maximum statutory fees
              established by the State of Florida. Travel, convenience,
              after-hours, rush, witness, signing-package, Apostille
              handling, courier, and similar service fees are{" "}
              <span className="text-white">separate</span> from statutory
              notarial fees and are disclosed in writing prior to the
              appointment. Published flat-rate signing packages are valid
              within standard service parameters; out-of-area travel,
              edocs-only / paper-only conversions, after-hours sessions, and
              rush turnarounds may carry additional charges as quoted.
            </p>
          </Block>

          <Block title="4. Identification & Right to Refuse">
            <p>
              We are required by law to confirm signer identity through
              acceptable identification and to confirm the signer&apos;s
              willingness and apparent understanding of the document. We
              reserve the right to refuse a notarial act when legal
              requirements are not met, including but not limited to
              insufficient identification, apparent incapacity, coercion,
              fraud indicators, blank or incomplete documents, or any
              circumstance that would violate Florida notary law. A refused
              notarial act does not entitle the requester to a refund of
              travel, convenience, or other disclosed service fees.
            </p>
          </Block>

          <Block title="5. Cancellations & Travel">
            <p>
              Appointments cancelled with insufficient notice, or that
              result in a completed trip without a notarial act (signer
              unavailable, missing ID, incomplete documents, or signer
              refusal), may be subject to a travel and convenience fee as
              disclosed in the appointment confirmation. Rescheduling
              requests are accommodated whenever reasonably possible.
            </p>
          </Block>

          <Block title="6. Remote Online Notarization (RON) Disclosures">
            <p>
              RON sessions are performed under our Florida commission using
              a state-approved audio-video and identity-verification
              technology provider. The remote signer may be located
              anywhere in the United States or internationally, subject to
              the laws of the signer&apos;s location and the receiving
              jurisdiction. The RON session is recorded and retained by the
              technology provider for the period required by Florida law.
              Some document types and jurisdictions are not eligible for
              RON; eligibility is confirmed prior to the session.
            </p>
          </Block>

          <Block title="7. Apostille & International Document Disclosures">
            <p>
              Apostille services involve obtaining a Florida Secretary of
              State Apostille certificate for documents destined for Hague
              Convention countries, or coordinating chain authentication
              (Florida → U.S. Department of State → destination consulate)
              for non-Hague countries. Turnaround times depend on Florida
              Secretary of State processing, consular processing, and
              courier transit, and are not within our control. We do not
              translate documents and do not guarantee acceptance by any
              foreign authority; acceptance is solely at the discretion of
              the receiving jurisdiction.
            </p>
          </Block>

          <Block title="8. Limitation of Liability">
            <p>
              To the maximum extent permitted by law, OGO Notary & Signing
              Services and OldGuyOutside, LLC are not liable for indirect,
              incidental, special, consequential, or punitive damages
              arising out of or related to your use of OGONotary.com or our
              services. Our cumulative liability for any claim shall not
              exceed the fees actually paid for the specific service giving
              rise to the claim. We carry an active Florida notary bond and
              Errors & Omissions (E&O) insurance — credential evidence is
              available to vendor partners on request.
            </p>
          </Block>

          <Block title="9. Website Use">
            <p>
              Content on OGONotary.com — including service pages, pricing
              guidance, app previews (&quot;NotaryLeadEngine&quot;, &quot;NotaryCalcPro&quot;,
              &quot;ExpenseBookPro&quot;, and others in development), and downloadable
              resources — is provided for general informational purposes.
              We make reasonable efforts to keep information accurate, but
              do not warrant that the site is error-free or continuously
              available. Unauthorized scraping, reverse engineering,
              automated harvesting of forms, or commercial reuse of site
              content is prohibited.
            </p>
          </Block>

          <Block title="10. Governing Law & Venue">
            <p>
              These terms are governed by the laws of the State of Florida,
              without regard to its conflict-of-laws principles. Venue for
              any dispute shall lie exclusively in the state or federal
              courts located in Brevard County, Florida.
            </p>
          </Block>

          <Block title="11. Contact">
            <p>
              Questions about these terms?
            </p>
            <ul className="mt-3 space-y-1 list-none">
              <li>
                <span className="text-white">Email:</span>{" "}
                <a href="mailto:Greg@ogonotary.com" className="text-[#D4AF37] hover:underline">
                  Greg@ogonotary.com
                </a>
              </li>
              <li>
                <span className="text-white">Phone:</span>{" "}
                <a href="tel:+18555054722" className="text-[#D4AF37] hover:underline">
                  (855) 505-4722
                </a>
              </li>
              <li>
                <span className="text-white">Web:</span>{" "}
                <a href="https://OGONotary.com" className="text-[#D4AF37] hover:underline">
                  OGONotary.com
                </a>
              </li>
              <li>
                <span className="text-white">Service Area:</span>{" "}
                Brevard County, Florida (in-person) · National &
                International (RON & Apostille, where lawful)
              </li>
            </ul>
          </Block>

          <p className="text-xs text-slate-500">
            Last updated: 2026. OGO Notary & Signing Services · An
            OldGuyOutside, LLC Company. These terms are provided as a
            general information template and do not constitute legal
            advice.
          </p>
        </div>
      </section>
    </>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <SectionEyebrow className="mb-3">{title}</SectionEyebrow>
      <div className="text-slate-300 leading-relaxed">{children}</div>
    </div>
  );
}
